// @ts-strict-ignore
import { addExperimentGroupToTargeting } from '@platforms/shared';
import {
	BaseServiceSetup,
	context,
	defineExperiment,
	Experiment,
	getExperiment,
	utils,
} from '@wikia/ad-engine';

type RunningExperimentType = Experiment | null | undefined;

const logGroup = 'UcpDesktopMidBoxAdExperiment';

/* Will be run alongside the ucp-mobile open web removal experiment */
export class UcpDesktopMidBoxAdExperiment extends BaseServiceSetup {
	private pathfinderExperimentVariantNames = {
		control: 'midboxad-control',
		active: 'midboxad-active',
	};

	private pathfinderExperimentVariants = [
		defineExperiment({
			name: this.pathfinderExperimentVariantNames.control,
			buckets: ['33', '34', '35', '36', '37', '38', '39'],
		}),
		defineExperiment({
			name: this.pathfinderExperimentVariantNames.active,
			buckets: ['40', '41', '42', '43', '44', '45', '46'],
		}),
	];

	call(): void {
		this.configure();
	}

	private pathfinderActiveExperimentVariant: RunningExperimentType = getExperiment(
		this.pathfinderExperimentVariants,
	);

	/* See src/platforms/ucp-desktop/ad-context.ts for why this needs to be inserted here.
	 * We need to add it here, because the baseContext is initialized before any of the BaseServices are added to the pipeline.
	 *
	 * Check window.ads.adContext.bidders.a9.bidsRefreshing.slots and
	 * window.ads.adContext.bidders.a9.bidsRefreshing.slots for the updated slots.
	 *
	 * These context values are used in src/ad-bidders/a9/index.ts and src/ad-bidders/prebid/prebid-data-tracking.ts
	 *
	 * The adengine_debug=1 query param needs be used to see the updated slots in the console.
	 * */
	private addMidBoxAdBidRefreshing(): void {
		['a9', 'prebid'].forEach((bidder) => {
			const bidderRefreshSlots = context.get(
				`bidders.${bidder}.bidsRefreshing.slots`,
			) as Array<string>;
			if (bidderRefreshSlots) {
				bidderRefreshSlots.push('mid_boxad');
				context.set(`bidders.${bidder}.bidsRefreshing.slots`, bidderRefreshSlots);
			}
		});
	}

	private configure() {
		if (this.isExperimentEnabled() && this.pathfinderActiveExperimentVariant?.name) {
			switch (this.pathfinderActiveExperimentVariant.name) {
				case this.pathfinderExperimentVariantNames.active:
					utils.logger(logGroup, 'Active group');
					context.set('experiments.midBoxAdExperiment', true);
					addExperimentGroupToTargeting(this.pathfinderExperimentVariantNames.active);
					this.addMidBoxAdBidRefreshing();
					break;
				case this.pathfinderExperimentVariantNames.control:
					utils.logger(logGroup, 'Control group');
					context.set('experiments.midBoxAdExperiment', false);
					addExperimentGroupToTargeting(this.pathfinderExperimentVariantNames.control);
					break;
			}
		} else {
			utils.logger(logGroup, 'Experiment disabled');
			context.set('experiments.midBoxAdExperiment', false);
			return;
		}
	}

	private isExperimentEnabled(): boolean {
		return this.instantConfig.get('icExperiments', []).includes('desktopMidBoxAdExperiment');
	}
}
